/**
 * M9 Communication REST client — DM, groups, broadcasts, push tokens.
 */

import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

export type ParticipantKind = 'USER' | 'FIELD_USER';
export type ConversationChannel = 'DIRECT' | 'GROUP' | 'BROADCAST';
export type MessageKind = 'TEXT' | 'SYSTEM' | 'ATTACHMENT';
export type BroadcastStatus = 'DRAFT' | 'SCHEDULED' | 'SENT' | 'CANCELLED';
export type BroadcastAudience = 'ALL' | 'ROLE' | 'ENGAGEMENT' | 'FIELD_USERS' | 'WEB_USERS';

export type Conversation = {
  id: string;
  channel: ConversationChannel;
  title: string | null;
  engagement_id: string | null;
  created_by_user_id: string | null;
  is_locked: boolean;
  last_message_at: string | null;
  last_message_preview: string | null;
  created_at: string;
  updated_at: string;
};

export type Participant = {
  id: string;
  conversation_id: string;
  participant_kind: ParticipantKind;
  participant_id: string;
  joined_at: string;
  last_read_at: string | null;
  is_muted: boolean;
  is_admin: boolean;
  left_at: string | null;
};

export type Message = {
  id: string;
  conversation_id: string;
  sender_kind: ParticipantKind;
  sender_id: string;
  kind: MessageKind;
  body: string;
  attachment_path: string | null;
  attachment_mime: string | null;
  reply_to_id: string | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
};

export type Broadcast = {
  id: string;
  title: string;
  body: string;
  audience: BroadcastAudience;
  audience_ref: Record<string, unknown>;
  status: BroadcastStatus;
  scheduled_at: string | null;
  sent_at: string | null;
  sent_by_user_id: string | null;
  recipient_count: number;
  delivered_count: number;
  read_count: number;
  requires_acknowledgement: boolean;
  deep_link: string | null;
  created_at: string;
};

export type BroadcastDelivery = {
  id: string;
  broadcast_id: string;
  recipient_kind: ParticipantKind;
  recipient_id: string;
  status: string;
  delivered_at: string | null;
  read_at: string | null;
  acknowledged_at: string | null;
};

export type PushToken = {
  id: string;
  principal_kind: ParticipantKind;
  principal_id: string;
  platform: string;
  device_label: string | null;
  last_seen_at: string;
  revoked_at: string | null;
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

export const commsApi = {
  listConversations: (limit = 200) => call<Conversation[]>(`/comms/conversations?limit=${limit}`),
  getConversation: (id: string) => call<Conversation>(`/comms/conversations/${id}`),
  createDirect: (other_kind: ParticipantKind, other_id: string) =>
    call<Conversation>('/comms/conversations:direct', {
      method: 'POST',
      body: JSON.stringify({ other_kind, other_id }),
    }),
  createGroup: (title: string, participants: [ParticipantKind, string][], engagement_id?: string) =>
    call<Conversation>('/comms/conversations:group', {
      method: 'POST',
      body: JSON.stringify({ title, participants, engagement_id }),
    }),

  listParticipants: (id: string) => call<Participant[]>(`/comms/conversations/${id}/participants`),

  listMessages: (id: string, limit = 100, before?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (before) q.set('before', before);
    return call<Message[]>(`/comms/conversations/${id}/messages?${q.toString()}`);
  },
  sendMessage: (
    id: string,
    body: string,
    opts?: Partial<{
      kind: MessageKind;
      attachment_path: string;
      attachment_mime: string;
      reply_to_id: string;
    }>,
  ) =>
    call<Message>(`/comms/conversations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, kind: 'TEXT', ...opts }),
    }),
  deleteMessage: (messageId: string) =>
    call<void>(`/comms/messages/${messageId}`, { method: 'DELETE' }),
  markRead: (id: string) =>
    call<{ status: string }>(`/comms/conversations/${id}:mark-read`, {
      method: 'POST',
    }),

  // Broadcasts
  listBroadcasts: (status?: BroadcastStatus) => {
    const q = status ? `?status=${status}` : '';
    return call<Broadcast[]>(`/comms/broadcasts${q}`);
  },
  listMyBroadcasts: () => call<Broadcast[]>('/comms/broadcasts/mine'),
  createBroadcast: (body: {
    title: string;
    body: string;
    audience: BroadcastAudience;
    audience_ref?: Record<string, unknown>;
    deep_link?: string;
    requires_acknowledgement?: boolean;
    scheduled_at?: string;
  }) =>
    call<Broadcast>('/comms/broadcasts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  sendBroadcast: (id: string, recipients: [ParticipantKind, string][]) =>
    call<Broadcast>(`/comms/broadcasts/${id}:send`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    }),
  cancelBroadcast: (id: string) =>
    call<Broadcast>(`/comms/broadcasts/${id}:cancel`, { method: 'POST' }),
  listDeliveries: (id: string) => call<BroadcastDelivery[]>(`/comms/broadcasts/${id}/deliveries`),

  // Push tokens
  listPushTokens: () => call<PushToken[]>('/comms/push-tokens'),
  registerPushToken: (token: string, platform: 'ios' | 'android' | 'web', label?: string) =>
    call<PushToken>('/comms/push-tokens', {
      method: 'POST',
      body: JSON.stringify({ token, platform, device_label: label }),
    }),
  revokePushToken: (token: string) =>
    call<void>(`/comms/push-tokens/${encodeURIComponent(token)}`, {
      method: 'DELETE',
    }),
};
